import { AxiosError } from 'axios';
import { handleApiError } from '../axios/helpers/errors';
import { baseRequest } from '../request-configs/base-request';

const DEFAULT_SAVE_CHUNK_LENGTH = 100;
const DEFAULT_DELETE_CHUNK_LENGTH = 25;

/**
 * When saving larger payload arrays, use this to avoid 413s from being thrown from
 * our APIs due to our request body being too large. This method will spread the
 * request out into multiple requests.
 * @param config Some configuration options, including `adminGatewayPath`
 * @returns The same array type given to `config.payload`.
 */
export const bulkSaveRequest = async <T extends Array<unknown>>(config: {
  url: string,
  method: keyof Pick<typeof baseRequest, 'post' | 'put'>,
  payload: T,
  chunkSize?: number,
  logChunks?: boolean,
}): Promise<T> => {
  const chunkSize = config.chunkSize ?? DEFAULT_SAVE_CHUNK_LENGTH;
  const numChunks = Math.floor(config.payload.length / chunkSize);
  const fullResp: unknown[] = [];

  for (let index = 0; index <= numChunks; index += 1) {
    const chunk = config.payload.slice(index * chunkSize, (index + 1) * chunkSize);

    if (config.logChunks) {
      // eslint-disable-next-line no-console
      console.info({ [`Chunk ${index}`]: chunk });
    }

    // eslint-disable-next-line no-await-in-loop
    const chunkResp = await baseRequest[config.method](config.url, chunk);

    fullResp.push(...chunkResp.data ?? []);
  }

  return fullResp as T;
};

export const bulkDeleteRequest = async <T extends Array<unknown>>(config: {
  url: string,
  ids: string,
  chunkSize?: number,
  logChunks?: boolean,
}): Promise<T> => {
  try {
    const ids = config.ids.split(',') || [];
    if (!ids.length) {
      return [] as unknown as T;
    }

    const chunkSize = config.chunkSize ?? DEFAULT_DELETE_CHUNK_LENGTH;
    const numChunks = Math.floor(ids.length / chunkSize);
    const fullResp: unknown[] = [];

    for (let index = 0; index <= numChunks; index += 1) {
      const chunk = ids.slice(index * chunkSize, (index + 1) * chunkSize);

      if (config.logChunks) {
      // eslint-disable-next-line no-console
        console.info({ [`Chunk ${index}`]: chunk });
      }

      // eslint-disable-next-line no-await-in-loop
      const chunkResp = await baseRequest.delete<{ data: T }>(`${config.url}/${chunk.join(',')}`);

      fullResp.push(...chunkResp.data ?? []);
    }

    return fullResp as T;
  } catch (err) {
    return handleApiError(err as AxiosError);
  }
};
